package com.dualremotetest.web

import com.dualremotetest.AudioLevels
import com.dualremotetest.Chiptune
import com.dualremotetest.EngineSound
import com.dualremotetest.Music
import com.dualremotetest.Sfx
import com.dualremotetest.TankAudio
import com.dualremotetest.nanoTime
import kotlinx.browser.window

/**
 * The browser's [TankAudio], through Web Audio. It keeps QueuedAudio's rules: effects are skipped while paused (except
 * the pause jingle), when muted, or when the same effect repeats within [AudioLevels.MIN_REPEAT_NS]; music cues never
 * overlap; one-shot cues report when they have ended. Sounds are the same built-in chiptune as on Android (rendered by
 * [Chiptune]), and the music files bundled with the Android app (audio/stage_start.mp3, …) replace the built-in cues.
 *
 * Safari keeps a page silent until the player first touches it or presses a key: [unlock] is called from those events.
 * While sound is still locked, one-shot cues count as ended, so the game never waits for a cue nobody can hear.
 */
class WebTankAudio : TankAudio {
    private val ctx: dynamic = js(
        "(typeof AudioContext !== 'undefined') ? new AudioContext() : " +
            "((typeof webkitAudioContext !== 'undefined') ? new webkitAudioContext() : null)",
    )
    private val sfxBuffers = arrayOfNulls<Any?>(Sfx.entries.size)
    private val musicBuffers = HashMap<Music, dynamic>()
    private val musicFromFile = HashSet<Music>()
    private var prepared = false
    private var unlocked = false

    private var musicGain = AudioLevels.gainFor(75)
    private var sfxGain = AudioLevels.gainFor(75)
    private val lastPlayed = LongArray(Sfx.entries.size) { Long.MIN_VALUE / 2 }
    private var gamePaused = false
    private var appPaused = false
    private val paused get() = gamePaused || appPaused

    var requested = 0L; private set
    var skipped = 0L; private set
    var musicStarts = 0L; private set
    private var lastMusic = Music.NONE
    private var engineState = EngineSound.OFF

    // The music cue: [cue] numbers each start, so a stale "ended" or a late file load is ignored.
    private var cue = 0
    private var current = Music.NONE
    private var sounding = Music.NONE
    private var musicSource: dynamic = null
    private var musicNode: dynamic = null
    /** Seconds into the cue: where it starts when its buffer arrives or playback resumes after a pause. */
    private var musicOffset = 0.0
    private var musicStartedAt = 0.0
    private var loopSource: dynamic = null

    init {
        if (ctx != null) {
            musicNode = ctx.createGain()
            musicNode.connect(ctx.destination)
            // Rendering every sound takes a moment; do it after the first frame is on screen.
            window.setTimeout({ prepare() }, 50)
        }
    }

    private fun prepare() {
        if (prepared || ctx == null) return
        prepared = true
        for (s in Sfx.entries) sfxBuffers[s.ordinal] = buffer(Chiptune.render(s))
        for (m in Music.entries) {
            if (m == Music.NONE) continue
            if (m.loops) musicBuffers[m] = buffer(Chiptune.music(m)) else loadFile(m)
        }
        if (current != Music.NONE && musicSource == null && !paused) startMusic()
        if (engineState != EngineSound.OFF) engine(engineState)
    }

    /** The bundled music file for [m], or the built-in chiptune cue when there is none or it cannot be decoded. */
    private fun loadFile(m: Music) {
        val builtIn = { if (musicBuffers[m] == null) musicBuffers[m] = buffer(Chiptune.music(m)); onMusicReady(m) }
        window.fetch("audio/${m.asset}.mp3").then { r ->
            if (!r.ok) {
                builtIn()
                null
            } else r.arrayBuffer().then { data ->
                val ok = { decoded: dynamic -> musicBuffers[m] = decoded; musicFromFile += m; onMusicReady(m) }
                val fail = { _: dynamic -> builtIn() }
                // The callback form also works on Safari versions whose decodeAudioData returns no promise.
                runCatching {
                    val p = ctx.decodeAudioData(data, ok, fail)
                    if (p != null && p.then != undefined) p.catch { _: dynamic -> } // already handled by fail
                }.onFailure { builtIn() }
            }
        }.catch { builtIn() }
    }

    private fun onMusicReady(m: Music) {
        if (current == m && musicSource == null && !paused) startMusic()
    }

    private fun buffer(pcm: ShortArray): dynamic {
        val b = ctx.createBuffer(1, maxOf(pcm.size, 1), Chiptune.RATE)
        val data = b.getChannelData(0)
        for (i in pcm.indices) data[i] = pcm[i] / 32768.0
        return b
    }

    /** Called from the first touches and key presses: Safari starts sound only from inside one. */
    fun unlock() {
        if (ctx == null) return
        if (ctx.state != "running") runCatching { ctx.resume() }
        if (!unlocked) {
            unlocked = true
            // Older iOS also wants a sound started within the gesture.
            val silent = ctx.createBufferSource()
            silent.buffer = ctx.createBuffer(1, 1, Chiptune.RATE)
            silent.connect(ctx.destination)
            silent.start(0)
            prepare()
        }
    }

    private val running get() = ctx != null && ctx.state == "running"

    /** The browser has not let the page make sound yet (it waits for a click, tap or key press). */
    val locked get() = ctx != null && ctx.state != "running"

    override fun sfx(s: Sfx) {
        requested++
        val now = nanoTime()
        if ((paused && s != Sfx.PAUSE) || sfxGain == 0f || now - lastPlayed[s.ordinal] < AudioLevels.MIN_REPEAT_NS || !running) {
            skipped++
            return
        }
        val buf = sfxBuffers[s.ordinal] ?: run { skipped++; return } // still preparing: skip rather than wait
        lastPlayed[s.ordinal] = now
        val src = ctx.createBufferSource()
        src.buffer = buf
        val g = ctx.createGain()
        g.gain.value = sfxGain * s.level
        src.connect(g)
        g.connect(ctx.destination)
        src.start(0)
    }

    override fun music(m: Music) {
        lastMusic = m
        stopMusicSource()
        cue++
        musicOffset = 0.0
        if (m == Music.NONE || (musicGain == 0f && !m.loops)) {
            // Muted one-shot cues are not started at all (a muted loop starts silently, as on Android).
            if (m != Music.NONE) skipped++
            current = Music.NONE
            sounding = Music.NONE
            return
        }
        musicStarts++
        current = m
        sounding = if (m.loops) Music.NONE else m
        if (!paused) startMusic()
    }

    private fun startMusic() {
        val m = current
        val buf = musicBuffers[m] ?: return // starts when its buffer is ready
        val n = cue
        val src = ctx.createBufferSource()
        src.buffer = buf
        src.loop = m.loops
        src.connect(musicNode)
        musicNode.gain.value = musicGain
        src.onended = { if (cue == n && musicSource === src && !m.loops) { sounding = Music.NONE; musicSource = null } }
        val duration = buf.duration as Double
        val offset = if (m.loops) musicOffset % duration else musicOffset
        if (!m.loops && offset >= duration) {
            sounding = Music.NONE
            return
        }
        src.start(0, offset)
        musicSource = src
        musicStartedAt = (ctx.currentTime as Double) - offset
    }

    private fun stopMusicSource() {
        val src = musicSource ?: return
        musicSource = null
        src.onended = null
        runCatching { src.stop() }
    }

    override fun isPlaying(m: Music) = m != Music.NONE && sounding == m && running

    override fun engine(s: EngineSound) {
        engineState = s
        val loop = when (s) {
            EngineSound.OFF -> null
            EngineSound.IDLE -> Sfx.ENGINE_IDLE
            EngineSound.MOVING -> Sfx.ENGINE_MOVE
        }
        setLoop(loop, sfxGain * (loop?.level ?: 0f))
    }

    private fun setLoop(s: Sfx?, gain: Float) {
        if (loopSource != null) runCatching { loopSource.stop() }
        loopSource = null
        if (s == null || gain <= 0f || paused || ctx == null) return
        val buf = sfxBuffers[s.ordinal] ?: return
        val src = ctx.createBufferSource()
        src.buffer = buf
        src.loop = true
        val g = ctx.createGain()
        g.gain.value = gain
        src.connect(g)
        g.connect(ctx.destination)
        src.start(0)
        loopSource = src
    }

    override fun setGamePaused(paused: Boolean) = setPaused(game = paused, app = appPaused)

    /** The page was hidden or shown again (the browser's counterpart of the Activity's onPause and onResume). */
    fun setAppPaused(paused: Boolean) {
        setPaused(game = gamePaused, app = paused)
        if (!paused && unlocked && ctx != null && ctx.state != "running") runCatching { ctx.resume() }
    }

    private fun setPaused(game: Boolean, app: Boolean) {
        val before = paused
        gamePaused = game
        appPaused = app
        if (paused == before || ctx == null) return
        if (paused) {
            // Remember where the cue was, then stop it and the engine; resuming carries on from there.
            if (musicSource != null) {
                musicOffset = (ctx.currentTime as Double) - musicStartedAt
                stopMusicSource()
            }
            if (loopSource != null) runCatching { loopSource.stop() }
            loopSource = null
        } else {
            if (current != Music.NONE && musicSource == null && (current.loops || sounding == current)) startMusic()
            engine(engineState)
        }
    }

    override fun setVolumes(musicPercent: Int, sfxPercent: Int) {
        musicGain = AudioLevels.gainFor(musicPercent)
        sfxGain = AudioLevels.gainFor(sfxPercent)
        if (musicNode != null) musicNode.gain.value = musicGain
        if (engineState != EngineSound.OFF) engine(engineState)
    }

    override fun statsText(): String {
        val state = if (ctx == null) "no Web Audio in this browser" else "Web Audio ${ctx.state}, ${ctx.sampleRate} Hz"
        val files = if (musicFromFile.isEmpty()) "all built-in chiptune" else "music files: " + musicFromFile.joinToString { it.asset }
        return "requests $requested · skipped $skipped (paused, muted, locked or repeated within " +
            "${AudioLevels.MIN_REPEAT_NS / 1_000_000} ms) · music cues started $musicStarts (last ${lastMusic.name}, still " +
            "sounding ${sounding.name}) · engine ${engineState.name} · $state · $files"
    }
}
